// Authentication layer — thin wrapper over Supabase Auth (email + password).
// Multi-tenant: each signup creates a Company (handled by the DB trigger
// `handle_new_user`), and the registrant becomes that company's Owner.
import { supabase } from "./supabaseClient";
import { normaliseEmail, isLikelyDisposable } from "./emailDomains";

function requireClient() {
  if (!supabase) throw new Error("Supabase is not configured (check .env).");
  return supabase;
}

// Register a new user + company. `companyName` is passed as auth metadata so the
// DB trigger can name the company and assign the owner membership.
//
// ⛔ THE DISPOSABLE-DOMAIN CHECK BELOW IS NOT THE GUARD. The guard is
// `trg_00_signup_email_policy`, a BEFORE INSERT trigger on auth.users
// (20261004120000_cc41_signup_integrity.sql), and it has to be: this function is
// not on the path from `POST /auth/v1/signup`, which is a public endpoint. What
// this buys is a straight answer without a round-trip, and a message written for
// someone filling in a form. If it disagrees with the database — and it will,
// because the table is extended by INSERT with no deploy — the database wins and
// the person simply hears it a moment later.
export async function signUp({ email, password, companyName, fullName }) {
  const client = requireClient();
  const cleanEmail = normaliseEmail(email);
  if (isLikelyDisposable(cleanEmail)) {
    throw new Error(
      "Disposable email addresses are not accepted. Please sign up with a permanent work or personal email address."
    );
  }
  const { data, error } = await client.auth.signUp({
    email: cleanEmail,
    password,
    options: {
      data: {
        company_name: (companyName || "").trim(),
        full_name: (fullName || "").trim(),
      },
    },
  });
  if (error) throw error;
  // data.session is null when "Confirm email" is enabled in Supabase Auth settings.
  return data;
}

// ── Email verification CODE (P4-CC41a) ──────────────────────────────────────
//
// ⛔ NO VERIFICATION-CODE TABLE, AND NOBODY SHOULD ADD ONE. Supabase Auth
// already issues the code, expires it, single-uses it, compares it in constant
// time and rate-limits per address. Re-implementing that is four correct things
// replaced by four new chances to be wrong, in the one place where being wrong
// is an account takeover.
//
// ⚠️ THE `type` VALUES ARE ASYMMETRIC AND THAT IS NOT A TYPO — BOTH WERE READ
// OUT OF THE INSTALLED @supabase/auth-js 2.108.2, NOT FROM MEMORY:
//
//   verifyOtp -> 'email'
//     GoTrueClient.d.ts:1096  "If an email address is used, the type can be one
//     of the following (note: `signup` and `magiclink` types are DEPRECATED)"
//     GoTrueClient.d.ts:1097  "`email` – Used when verifying an OTP sent to the
//     user's email during sign-up or sign-in."
//     GoTrueClient.d.ts:1106  the library's own worked example, titled "Verify
//     Signup One-Time Password (OTP)":
//       supabase.auth.verifyOtp({ email, token, type: 'email' })
//
//   resend -> 'signup'
//     types.d.ts:694-695  ResendParams narrows the union to
//       type: Extract<EmailOtpType, 'signup' | 'email_change'>
//     so 'email' is NOT assignable here — 'signup' is the only value that means
//     "send the signup confirmation again".
//
// 'signup' still appears in EmailOtpType (types.d.ts:693) and still works
// server-side, so passing it to verifyOtp would not visibly fail today. It is
// deprecated, which is exactly the kind of thing that keeps working until it
// doesn't.

/**
 * Confirm a signup with the 6-digit code that was emailed.
 * On success Supabase establishes a session and AuthProvider's existing
 * onAuthStateChange listener takes it from there — this returns the data and
 * does no session handling of its own.
 */
export async function verifySignupCode({ email, token }) {
  const client = requireClient();
  const { data, error } = await client.auth.verifyOtp({
    email: normaliseEmail(email),
    token: String(token || "").trim(),
    type: "email",
  });
  // ⛔ THE ERROR IS THROWN UNTOUCHED, AND CC26c-1g IS WHY. Three separate
  // mappers each "improved" a message and between them swallowed the one
  // sentence that carried the instruction. Supabase's own wording here already
  // distinguishes an expired code from a wrong one from too many attempts, and
  // that distinction is the entire value. If a message needs improving, improve
  // it in the SCREEN, where there is enough context to know what to say.
  if (error) throw error;
  return data;
}

/** Send the signup confirmation code again. */
export async function resendSignupCode({ email }) {
  const client = requireClient();
  const { data, error } = await client.auth.resend({
    type: "signup",
    email: normaliseEmail(email),
  });
  if (error) throw error;   // untouched — see verifySignupCode above
  return data;
}

export async function signIn({ email, password }) {
  const client = requireClient();
  const { data, error } = await client.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data;
}

export async function signOut() {
  const client = requireClient();
  const { error } = await client.auth.signOut();
  if (error) throw error;
}

export async function getSession() {
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  return data.session;
}

// Subscribe to auth changes; returns an unsubscribe function.
export function onAuthChange(callback) {
  if (!supabase) return () => {};
  const { data } = supabase.auth.onAuthStateChange((_event, session) => callback(session));
  return () => data.subscription.unsubscribe();
}

// The current user's OWN company membership. company_members_select
// deliberately allows a member to SELECT the whole roster of their company
// (Team Members needs to show everyone) — so this query must filter by
// user_id ITSELF and must never rely on RLS to identify "me". Without that
// filter, ordering by created_at + limit(1) returns the OLDEST row in the
// company (typically the founder's owner row) the moment a company has more
// than one member, handing the caller someone else's identity.
// userId is optional — pass it when already known (e.g. from a session you
// already have); otherwise it's read from the LOCAL session via
// getSession(), not getUser(), since that would be an unnecessary network
// round-trip. Returns { company_id, role, company_name } or null if no id
// can be resolved at all — this never falls back to an unfiltered query.
export async function getMyMembership(userId) {
  if (!supabase) return null;
  let uid = userId;
  if (!uid) {
    const { data } = await supabase.auth.getSession();
    uid = data?.session?.user?.id;
  }
  if (!uid) return null;
  const { data, error } = await supabase
    .from("company_members")
    .select("company_id, role, companies(name)")
    .eq("user_id", uid)
    .order("created_at") // deterministic tie-break for CC13b's multi-company case, now that it's scoped to this user's own rows
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return {
    company_id: data.company_id,
    role: data.role,
    company_name: data.companies?.name || null,
  };
}

// The customer-facing business name — NOT companies.name, which is a
// write-once signup artefact (handle_new_user() names it once from signup
// metadata; no screen ever updates it after). Precedence: trading_name ->
// legal_name -> null (the caller falls back further, to companies.name/
// membership.company_name, since this function doesn't know either).
// Mirrored server-side in invite_preview() — see
// 20260730120000_invite_preview_business_name.sql; the two must not drift.
export async function getBusinessName(companyId) {
  if (!supabase) return null;
  const { data, error } = await supabase
    .from("business_settings")
    .select("trading_name, legal_name")
    .eq("company_id", companyId)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  const trading = (data.trading_name || "").trim();
  if (trading) return trading;
  const legal = (data.legal_name || "").trim();
  return legal || null;
}

// Preview a pending invite by its plaintext token — used by AuthScreen before the
// visitor has an account (invite_preview is granted to `anon`). Lives here, not in
// team.js: this is a pre-login concern consumed by AuthScreen, and a not-yet-
// authenticated screen importing a team-management module is the wrong dependency
// direction. Returns { company_name, role, email, status, expired } or null for an
// unknown token — never an error, so nothing distinguishes a wrong token from no token.
export async function invitePreview(token) {
  if (!supabase) return null;
  const { data, error } = await supabase.rpc("invite_preview", { p_token: token }).maybeSingle();
  if (error) throw error;
  return data || null;
}
